import * as React from "react"
import type { Participant } from "livekit-client"
import { Track } from "livekit-client"
import type {
    ParticipantClickEvent,
    TrackReferenceOrPlaceholder,
} from "@livekit/components-core"
import {
    isTrackReference,
    isTrackReferencePinned,
} from "@livekit/components-core"
import {
    ConnectionQualityIndicator,
    FocusToggle,
    LockLockedIcon,
    ParticipantContext,
    TrackRefContext,
    useEnsureTrackRef,
    useFeatureContext,
    useIsEncrypted,
    useMaybeLayoutContext,
    useMaybeParticipantContext,
    useMaybeTrackRefContext,
    useParticipantTile,
    useTrackMutedIndicator,
    VideoTrack,
    useIsMuted,
} from "@livekit/components-react"
import { UsernameComponent } from "../../video-call/UsernameComponent"
import { View } from "react-native"
import { theme } from "../../theme/theme"
import LiveAvatarM from "../../../tutor/students/LiveAvatarM"
import { LinearGradient } from "expo-linear-gradient"
// @ts-ignore
import { avatarColors } from "@data/constants/studentsConstants"
import { hasRole } from "../../../data/utility/ability"
import { useIsSoundMode } from "../../hooks/livekit/useParticipantState"

/**
 * The `ParticipantContextIfNeeded` component only creates a `ParticipantContext`
 * if there is no `ParticipantContext` already.
 * @example
 * ```tsx
 * <ParticipantContextIfNeeded participant={trackReference.participant}>
 *  ...
 * </ParticipantContextIfNeeded>
 * ```
 * @public
 */
export function ParticipantContextIfNeeded(
    props: React.PropsWithChildren<{
        participant?: Participant
    }>
) {
    const hasContext = !!useMaybeParticipantContext()
    return props.participant && !hasContext ? (
        <ParticipantContext.Provider value={props.participant}>
            {props.children}
        </ParticipantContext.Provider>
    ) : (
        <>{props.children}</>
    )
}

/**
 * Only create a `TrackRefContext` if there is no `TrackRefContext` already.
 * @internal
 */
export function TrackRefContextIfNeeded(
    props: React.PropsWithChildren<{
        trackRef?: TrackReferenceOrPlaceholder
    }>
) {
    const hasContext = !!useMaybeTrackRefContext()
    return props.trackRef && !hasContext ? (
        <TrackRefContext.Provider value={props.trackRef}>
            {props.children}
        </TrackRefContext.Provider>
    ) : (
        <>{props.children}</>
    )
}

/** @public */
export interface ParticipantTileProps
    extends React.HTMLAttributes<HTMLDivElement> {
    /** The track reference to display. */
    trackRef?: TrackReferenceOrPlaceholder
    disableSpeakingIndicator?: boolean
    participants?: any[]
    onParticipantClick?: (event: ParticipantClickEvent) => void
    miniMode?: boolean
    noOutOfApp?: boolean
    noDisplayName?: boolean
    style?: React.CSSProperties
    key?: string
}

/**
 * The `ParticipantTile` component is the base utility wrapper for displaying a visual representation of a participant.
 * This component can be used as a child of the `TrackLoop` component or by passing a track reference as property.
 *
 * @example Using the `ParticipantTile` component with a track reference:
 * ```tsx
 * <ParticipantTile trackRef={trackRef} />
 * ```
 * @example Using the `ParticipantTile` component as a child of the `TrackLoop` component:
 * ```tsx
 * <TrackLoop>
 *  <ParticipantTile />
 * </TrackLoop>
 * ```
 * @public
 */
export const ParticipantTileM: (
    props: ParticipantTileProps & React.RefAttributes<HTMLDivElement>
) => React.ReactNode = /* @__PURE__ */ React.forwardRef<
    HTMLDivElement,
    ParticipantTileProps
>(function ParticipantTile(
    {
        trackRef,
        children,
        onParticipantClick,
        disableSpeakingIndicator,
        participants,
        miniMode,
        noOutOfApp = true,
        noDisplayName,
        style,
        ...htmlProps
    }: ParticipantTileProps,
    ref
) {
    const trackReference = useEnsureTrackRef(trackRef)
    const { isMuted } = useTrackMutedIndicator(trackRef)

    const { elementProps } = useParticipantTile<HTMLDivElement>({
        htmlProps,
        disableSpeakingIndicator,
        onParticipantClick,
        trackRef: trackReference,
    })

    const layoutContext = useMaybeLayoutContext()
    // @ts-ignore
    const user = participants?.find(
        (u) =>
            u?.id.toString() ===
            trackReference.participant.identity?.split(".")[0]
    )
    const isSpeaking = trackReference.participant.isSpeaking
    const speakerStyles = isSpeaking
        ? {
              borderColor: theme.colors.success,
              borderWidth: 1,
          }
        : {}
    const autoManageSubscription = useFeatureContext()?.autoSubscription

    const handleSubscribe = React.useCallback(
        (subscribed: boolean) => {
            if (
                trackReference.source &&
                !subscribed &&
                layoutContext &&
                layoutContext.pin.dispatch &&
                isTrackReferencePinned(trackReference, layoutContext.pin.state)
            ) {
                layoutContext.pin.dispatch({ msg: "clear_pin" })
            }
        },
        [trackReference, layoutContext]
    )

    // Per-participant sound mode (for visual badge on UsernameComponent)
    const participantIsSoundModeRedux = useIsSoundMode(user?.id)
    const participantIsSoundMode = user?.is_tutor || participantIsSoundModeRedux

    // @ts-ignore
    return (
        <div
            ref={ref}
            style={{
                position: "relative",
                height: "100%",
                cursor: "pointer",
                ...style,
            }}
            {...elementProps}
        >
            <TrackRefContextIfNeeded trackRef={trackReference}>
                <ParticipantContextIfNeeded
                    participant={trackReference.participant}
                >
                    {children ?? (
                        <>
                            {noOutOfApp && isMuted &&  (
                                <LinearGradient
                                    colors={[
                                        theme.colors.primary,
                                        theme.colors.blackAlmost,
                                    ]}
                                    style={{
                                        position: "absolute",
                                        width: "100%",
                                        height: "100%",
                                    }}
                                />
                            )}
                            {isTrackReference(trackReference) &&
                            !isMuted &&
                            (trackReference.publication?.kind === "video" ||
                                trackReference.source === Track.Source.Camera ||
                                trackReference.source ===
                                    Track.Source.ScreenShare) ? (
                                        <>
                                            <VideoTrack
                                                trackRef={trackReference}
                                                onSubscriptionStatusChanged={
                                                    handleSubscribe
                                                }
                                                manageSubscription={autoManageSubscription}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "contain",
                                                    maxHeight: "100%",
                                                    maxWidth: "100%",
                                                    backgroundColor:theme.colors.transparent,
                                                    zIndex: 1,
                                                }}
                                            />
                                        </>

                            ) : null}
                            <div
                                className="lk-participant-placeholder"
                                style={{
                                    height: "100%",
                                    backgroundColor: theme.colors.transparent,
                                }}
                            >
                                <View
                                    style={{
                                        height: "100%",
                                        width: "100%",
                                        justifyContent: "center",
                                        marginBottom: 10,
                                        zIndex: 1,
                                    }}
                                >
                                    <LiveAvatarM
                                        user={user}
                                        size={40}
                                        noOutOfApp={noOutOfApp}
                                        style={{ alignSelf: "center" }}
                                    />
                                </View>
                            </div>
                            <View
                                style={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 3,
                                    flexDirection: "row",
                                    alignItems: "center",
                                    zIndex: 1,
                                }}
                            >
                                <div>
                                    {trackReference.source ===
                                    Track.Source.Camera ? (
                                        <>
                                            <UsernameComponent
                                                userObject={user}
                                                user={user}
                                                noDisplayName={noDisplayName}
                                                speakerStyles={speakerStyles}
                                                soundMode={participantIsSoundMode}
                                                trackRef={trackReference}
                                                condition={
                                                    !trackReference.participant
                                                        .isMicrophoneEnabled
                                                }
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <UsernameComponent
                                                userObject={user}
                                                user={user}
                                                noDisplayName={noDisplayName}
                                                speakerStyles={speakerStyles}
                                                isScreenShare={
                                                    trackReference.source ===
                                                    Track.Source.ScreenShare
                                                }
                                                soundMode={participantIsSoundMode}
                                                trackRef={trackReference}
                                                condition={
                                                    !trackReference.participant
                                                        .isMicrophoneEnabled
                                                }
                                            />
                                        </>
                                    )}
                                </div>
                            </View>
                            <ConnectionQualityIndicator
                                className="lk-participant-metadata-item"
                                style={{
                                    borderRadius: 10,
                                    color: theme.colors.white,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "absolute",
                                    bottom: 23,
                                    left: 0,
                                    zIndex: 1,
                                }}
                            />
                        </>
                    )}
                    <FocusToggle trackRef={trackReference} />
                </ParticipantContextIfNeeded>
            </TrackRefContextIfNeeded>
        </div>
    )
})
